import React, { useState, useEffect } from 'react';
import { Negociacao, NegociacaoStage } from '../../../../types';
import { CheckCircle, ChevronDown } from 'lucide-react';
import LoadingSpinner from '../../../../src/components/LoadingSpinner';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { logNegotiationAction, logUsageEvent } from '../../../../src/lib/logger';
import { nomeEtapaNaTela } from '../../../../src/lib/nomeEtapaNaTela';

interface DetailHeaderProps {
    negotiation: Negociacao;
    onUpdate: () => void;
}

const DetailHeader: React.FC<DetailHeaderProps> = ({ negotiation, onUpdate }) => {
    const { user } = useAuth(); // Get current user for logging

    const [pipelines, setPipelines] = useState<{ id: string; name: string }[]>([]);
    const [stages, setStages] = useState<{ id: string; name: string; position: number }[]>([]);
    const [loadingStages, setLoadingStages] = useState(false);
    const [showPipelineDropdown, setShowPipelineDropdown] = useState(false);

    const [stats, setStats] = useState({
        daysInStage: 0,
        interactions: 0,
        daysWithoutInteraction: 0
    });

    useEffect(() => {
        fetchPipelines();
    }, []);

    useEffect(() => {
        if (negotiation.pipeline_id) {
            fetchStages(negotiation.pipeline_id);
        }
    }, [negotiation.pipeline_id]);

    useEffect(() => {
        fetchStats();
    }, [negotiation]);

    const fetchPipelines = async () => {
        const { data } = await supabase.from('pipelines').select('id, name').order('created_at');
        if (data) setPipelines(data);
    };

    const fetchStages = async (pipelineId: string) => {
        setLoadingStages(true);
        const { data } = await supabase
            .from('pipeline_stages')
            .select('*')
            .eq('pipeline_id', pipelineId)
            .order('position');

        if (data) setStages(data);
        setLoadingStages(false);
    };

    // Find current index based on stage NAME (since negotiation.stage is string)
    // If stages not loaded yet, default to -1
    const currentStepIndex = stages.findIndex(s => s.name === negotiation.stage);

    const handlePipelineChange = async (pipeline: { id: string; name: string }) => {
        if (pipeline.id === negotiation.pipeline_id) {
            setShowPipelineDropdown(false);
            return;
        }

        try {
            // Find old pipeline name
            const oldPipelineName = pipelines.find(p => p.id === negotiation.pipeline_id)?.name || 'Funil anterior';

            // Fetch first stage of new pipeline
            const { data: stagesData } = await supabase
                .from('pipeline_stages')
                .select('*')
                .eq('pipeline_id', pipeline.id)
                .order('position')
                .limit(1)
                .single();

            if (!stagesData) throw new Error('Funil sem etapas.');

            const { error } = await supabase
                .from('negociacoes')
                .update({
                    pipeline_id: pipeline.id,
                    stage: stagesData.name,
                    updated_at: new Date().toISOString()
                })
                .eq('id', negotiation.id);

            if (error) throw error;

            // Log Action (Clean String)
            await logNegotiationAction(
                negotiation.id,
                user?.id,
                'moveu',
                `do funil "${oldPipelineName}" para "${pipeline.name}"`
            );
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_pipeline_negociacao', origem: 'acao' });

            setShowPipelineDropdown(false);
            onUpdate();
        } catch (error) {
            console.error('Error changing pipeline:', error);
            alert('Erro ao trocar de funil.');
        }
    };

    const fetchStats = async () => {
        const today = new Date();

        // 1. Days in Stage
        // Use stage_changed_at if available, otherwise fallback to created_at (if first stage) or updated_at
        // A decent fallback for legacy data is updated_at
        const stageDate = negotiation.stage_changed_at
            ? new Date(negotiation.stage_changed_at)
            : new Date(negotiation.updated_at);

        const diffStageTime = today.getTime() - stageDate.getTime();
        const daysInStage = Math.floor(diffStageTime / (1000 * 3600 * 24));

        try {
            // 2. Interactions & Recency
            // Fetch counts
            const notesPromise = supabase
                .from('negociacao_notas')
                .select('created_at', { count: 'exact' })
                .eq('negociacao_id', negotiation.id)
                .order('created_at', { ascending: false })
                .limit(1);

            const tasksPromise = supabase
                .from('tarefas')
                .select('updated_at', { count: 'exact' })
                .eq('negociacao_id', negotiation.id)
                .order('updated_at', { ascending: false })
                .limit(1);

            const [notesRes, tasksRes] = await Promise.all([notesPromise, tasksPromise]);

            const notesCount = notesRes.count || 0;
            const tasksCount = tasksRes.count || 0;
            const totalInteractions = notesCount + tasksCount;

            // Recency
            let lastInteractionTime = new Date(negotiation.created_at).getTime(); // Default to creation

            if (notesRes.data && notesRes.data.length > 0) {
                const noteTime = new Date(notesRes.data[0].created_at).getTime();
                if (noteTime > lastInteractionTime) lastInteractionTime = noteTime;
            }
            if (tasksRes.data && tasksRes.data.length > 0) {
                const taskTime = new Date(tasksRes.data[0].updated_at).getTime();
                if (taskTime > lastInteractionTime) lastInteractionTime = taskTime;
            }

            const diffInteractionTime = today.getTime() - lastInteractionTime;
            const daysWithout = Math.floor(diffInteractionTime / (1000 * 3600 * 24));

            setStats({
                daysInStage: Math.max(0, daysInStage),
                interactions: totalInteractions,
                daysWithoutInteraction: Math.max(0, daysWithout)
            });

        } catch (error) {
            console.error("Error fetching stats", error);
        }
    };

    const handleStageChange = async (newStageName: string) => {
        if (newStageName === negotiation.stage) return;

        // Old stage name is negotiation.stage
        const oldStageName = negotiation.stage;

        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({ stage: newStageName }) // Trigger will update stage_changed_at
                .eq('id', negotiation.id);

            if (error) throw error;

            // Log Action
            await logNegotiationAction(
                negotiation.id,
                user?.id,
                'moveu',
                `da etapa "${oldStageName}" para "${newStageName}"`
            );
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mudar_etapa_detalhe', origem: 'acao' });

            onUpdate();
        } catch (error) {
            console.error('Error updating stage:', error);
            alert('Erro ao atualizar etapa.');
        }
    };

    const daysOpen = Math.floor((new Date().getTime() - new Date(negotiation.created_at).getTime()) / (1000 * 3600 * 24));

    return (
        <div className="space-y-6">
            {/* Top Row: Pipeline Selector & Stepper */}
            <div className="hidden lg:flex flex-col gap-4">

                {/* Pipeline Switcher */}
                <div className="relative self-start z-20">
                    <button
                        onClick={() => setShowPipelineDropdown(!showPipelineDropdown)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-slate-300 hover:text-primary transition-colors bg-gray-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 hover:border-primary/30"
                    >
                        <span>{pipelines.find(p => p.id === negotiation.pipeline_id)?.name || 'Selecionar Funil'}</span>
                        <ChevronDown size={14} className={`transition-transform duration-300 ${showPipelineDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showPipelineDropdown && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowPipelineDropdown(false)}></div>
                            <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-100 dark:border-slate-700/50 p-2 z-30 animate-fade-in origin-top-left">
                                <div className="text-xs font-bold text-gray-400 dark:text-slate-500 px-2 py-1 uppercase tracking-wide">Mover para Funil</div>
                                {pipelines.map(pipeline => (
                                    <button
                                        key={pipeline.id}
                                        onClick={() => handlePipelineChange(pipeline)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between
                                            ${negotiation.pipeline_id === pipeline.id
                                                ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                                                : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800/80 dark:bg-slate-800 font-medium'
                                            }
                                        `}
                                    >
                                        {pipeline.name}
                                        {negotiation.pipeline_id === pipeline.id && <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Stepper - Centered */}
                <div className="flex items-center justify-center w-full overflow-x-auto pb-2 scrollbar-hide">
                    <div className="flex items-center min-w-max">
                        {loadingStages ? (
                            <LoadingSpinner label="Carregando etapas..." size="sm" />
                        ) : stages.map((stage, index) => {
                            const isActive = index === currentStepIndex;
                            const isCompleted = index < currentStepIndex;

                            return (
                                <React.Fragment key={stage.id}>
                                    <div
                                        onClick={() => handleStageChange(stage.name)}
                                        className={`flex items-center gap-2 cursor-pointer group select-none transition-colors px-2
                                            ${isActive ? 'font-bold text-gray-800 dark:text-slate-100' : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 dark:text-slate-300'}
                                        `}
                                    >
                                        <div className={`
                                            w-8 h-8 rounded-full flex items-center justify-center border-2 text-sm transition-all shrink-0
                                            ${isActive ? 'border-primary text-primary bg-primary/10 dark:bg-primary/20' :
                                                isCompleted ? 'border-green-500 bg-green-500 text-white' : 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-500 group-hover:border-gray-300 dark:hover:border-slate-600 dark:border-slate-600'}
                                        `}>
                                            {isCompleted ? <CheckCircle size={16} /> : index + 1}
                                        </div>
                                        <span className="text-sm whitespace-nowrap">{nomeEtapaNaTela(stage.name)}</span>
                                    </div>
                                    {index < stages.length - 1 && (
                                        <div className={`h-[2px] w-8 sm:w-16 mx-1 ${index < currentStepIndex ? 'bg-green-500' : 'bg-gray-100 dark:bg-slate-800/80'}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* KPIs Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 bg-white dark:bg-slate-900">
                <div className="p-3 border border-gray-100 dark:border-slate-700/50 rounded-xl text-center bg-gray-50 dark:bg-slate-800/50 dark:bg-slate-800/50">
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100">{daysOpen}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Dias aberto</div>
                </div>
                <div className="p-3 border border-gray-100 dark:border-slate-700/50 rounded-xl text-center bg-gray-50 dark:bg-slate-800/50 dark:bg-slate-800/50">
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100">{stats.daysInStage}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Dias na fase</div>
                </div>
                <div className="p-3 border border-gray-100 dark:border-slate-700/50 rounded-xl text-center bg-gray-50 dark:bg-slate-800/50 dark:bg-slate-800/50">
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100">{stats.interactions}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Interações</div>
                </div>
                <div className="p-3 border border-gray-100 dark:border-slate-700/50 rounded-xl text-center bg-gray-50 dark:bg-slate-800/50 dark:bg-slate-800/50">
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100">{stats.daysWithoutInteraction}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Dias s/ Interação</div>
                </div>
            </div>
        </div>
    );
};

export default DetailHeader;
